import { FAMILY_QUESTIONS, LIFE_QUESTIONS, coverQuizSignals, optionsFor } from './coverQuizData';
import type { CoverQuizAnswers, CoverRole } from './coverQuizData';
import { L, listJoin } from './quizLang';
import type { QuizLang, Txt } from './quizLang';

/**
 * 保障小測驗 · 你的測驗結果 — what the CLIENT gets back.
 *
 * `coverQuizSignals` is the adviser's reading of the same answers; this is the
 * client's, and it keeps to what they told us: who depends on them, what
 * changed this year, what they want to understand. The 真假挑戰 answers are
 * explained on the spot during the quiz, so the report does not repeat them. It
 * names areas worth a look, never a product (see the client-quiz rule: fun,
 * not a sales funnel).
 */

export type ReportPriority = { key: string; icon: string; title: string; why: string };
export type ReportTip = { key: string; icon: string; text: string };

export type CoverClientReport = {
  headline: string;
  /** At most three, most pressing first. */
  priorities: ReportPriority[];
  /** At most three money habits, drawn from how they answered. */
  tips: ReportTip[];
  /** C3, the ten-year picture — part 2 can put a number on it. */
  dream: { icon: string; label: string } | null;
};

const PRIORITY_MAX = 3;
const TIP_MAX = 3;

const LIFE_PRIORITIES: Record<string, { icon: string; title: Txt; why: Txt }> = {
  newborn: {
    icon: '👶',
    title: L('新成員的保障', '新成员的保障', 'Cover for the new arrival'),
    why: L(
      '家中添了新成員，開支和責任都增加了，正是重新檢視保障的好時機。',
      '家中添了新成员，开支和责任都增加了，正是重新检视保障的好时机。',
      'A new family member means more costs and responsibility: a good time to review your cover.'
    )
  },
  home: {
    icon: '🏠',
    title: L('供樓與保障', '供楼与保障', 'Your mortgage and cover'),
    why: L(
      '有了按揭，即使收入中斷，每月供款仍然要繼續。',
      '有了按揭，即使收入中断，每月供款仍然要继续。',
      'With a mortgage, the monthly repayments carry on even if your income stops.'
    )
  },
  job: {
    icon: '💼',
    title: L('轉職後的醫療保障', '转职后的医疗保障', 'Medical cover after changing jobs'),
    why: L(
      '公司提供的醫療保障隨工作而來，轉職後可能會改變或出現空檔。',
      '公司提供的医疗保障随工作而来，转职后可能会改变或出现空档。',
      'Medical cover from an employer comes with the job, so it may change or leave a gap when you move.'
    )
  },
  business: {
    icon: '🚀',
    title: L('創業後的保障', '创业后的保障', 'Cover after starting a business'),
    why: L(
      '創業後不再有公司提供的醫療保障，收入也較不穩定，自己的保障更要安排好。',
      '创业后不再有公司提供的医疗保障，收入也较不稳定，自己的保障更要安排好。',
      'Running your own business means no employer medical cover and a less steady income, so your own cover matters more.'
    )
  },
  relationship: {
    icon: '💍',
    title: L('兩個人的規劃', '两个人的规划', 'Planning for two'),
    why: L(
      '開始新生活，雙方的保障和儲蓄目標值得一起談談。',
      '开始新生活，双方的保障和储蓄目标值得一起谈谈。',
      "Starting a new life together, it's worth talking over both your cover and savings goals."
    )
  },
  school: {
    icon: '🎒',
    title: L('升學開支', '升学开支', 'School costs'),
    why: L(
      '孩子升學，教育開支會逐步增加，早一點預算會更從容。',
      '孩子升学，教育开支会逐步增加，早一点预算会更从容。',
      'As your children move up, education costs grow; budgeting early makes it easier.'
    )
  },
  study: {
    icon: '🎓',
    title: L('進修期間的安排', '进修期间的安排', 'While you study'),
    why: L(
      '進修期間收入或會減少，預留一筆應急錢會更安心。',
      '进修期间收入或会减少，预留一笔应急钱会更安心。',
      'Income may drop while you study, so an emergency fund brings peace of mind.'
    )
  }
};

const DEPENDANT_KEYS = ['kids', 'partner', 'parents'];

/** The headline for each 身分; records without a role fall back to the persona's. */
const ROLE_HEADLINES: Record<CoverRole, Txt> = {
  fresh: L(
    '剛開始工作，正是打好基礎的時候：越早開始，保費和儲蓄的壓力都越小。',
    '刚开始工作，正是打好基础的时候：越早开始，保费和储蓄的压力都越小。',
    'Just starting work is the time to build a base: the earlier you start, the lighter premiums and saving feel.'
  ),
  single: L(
    '一個人生活更自由，也更要靠自己：病了、傷了，收入和開支都由你一個人承擔。',
    '一个人生活更自由，也更要靠自己：病了、伤了，收入和开支都由你一个人承担。',
    'Living on your own is freedom, and self-reliance: if you fall ill or get hurt, your income and costs are yours alone.'
  ),
  couple: L(
    '二人世界，一起規劃最有效：先保障好彼此，再一步步向共同目標出發。',
    '二人世界，一起规划最有效：先保障好彼此，再一步步向共同目标出发。',
    'As a couple, planning together works best: protect each other first, then work towards shared goals step by step.'
  ),
  youngKids: L(
    '孩子還小，家庭的開支和責任正在增加：無論發生甚麼事，孩子的將來都不應受影響。',
    '孩子还小，家庭的开支和责任正在增加：无论发生什么事，孩子的将来都不应受影响。',
    "With young children, family costs and responsibilities are growing: whatever happens, their future shouldn't be affected."
  ),
  schoolKids: L(
    '孩子升學的開支陸續來到：預早準備，家庭的日常生活就不用因此收緊。',
    '孩子升学的开支陆续来到：预早准备，家庭的日常生活就不用因此收紧。',
    "School costs are on their way: prepare early and the family won't have to tighten its belt."
  ),
  preRetire: L(
    '退休不遠了：現在最重要的是醫療開支有準備，退休後每月的生活費有着落。',
    '退休不远了：现在最重要的是医疗开支有准备，退休后每月的生活费有着落。',
    'Retirement is not far off: what matters most now is being ready for medical costs and having monthly living costs covered.'
  )
};

const COPY = {
  incomeTitle: L('家人的生活保障', '家人的生活保障', "Protecting your family's living"),
  incomeWhy: (who: string) =>
    L(
      `${who}依靠你。如果因病或意外暫時不能工作，家中的開支由甚麼支持？`,
      `${who}依靠你。如果因病或意外暂时不能工作，家中的开支由什么支持？`,
      `${who} rely on you. If illness or an accident kept you from working for a while, what would cover the household costs?`
    ),
  learn: (what: string) => L(`了解${what}`, `了解${what}`, `Learn more: ${what}`),
  firstInterestWhy: L(
    '這是你表示最想了解的範疇，先弄清楚它保障甚麼、不保障甚麼。',
    '这是你表示最想了解的范畴，先弄清楚它保障什么、不保障什么。',
    "You said this is what you most want to learn about: start with what it covers and what it doesn't."
  ),
  moreInterestWhy: L(
    '你也想了解這個範疇，可以和第一項一併檢視。',
    '你也想了解这个范畴，可以和第一项一并检视。',
    'You want to learn about this too; it can be reviewed together with the first.'
  ),
  medCiTitle: L('分清醫療與危疾', '分清医疗与危疾', 'Medical vs critical illness'),
  medCiWhy: L(
    '兩者作用不同：醫療保險報銷住院費用，危疾保險在確診時一筆過賠償。',
    '两者作用不同：医疗保险报销住院费用，危疾保险在确诊时一笔过赔偿。',
    'They do different jobs: medical insurance reimburses hospital bills; critical illness insurance pays a lump sum on diagnosis.'
  ),
  inflation: L(
    '存起來最安心，但長遠要留意通脹：以每年 3% 計，今天的 100 萬，約 24 年後的購買力只剩一半。',
    '存起来最安心，但长远要留意通胀：以每年 3% 计，今天的 100 万，约 24 年后的购买力只剩一半。',
    'Saving feels safest, but watch inflation over the long run: at 3% a year, $1M today buys only half as much in about 24 years.'
  ),
  ledgerStart: L(
    '試試記錄一個月的開支，知道錢花在哪裏，是規劃的第一步。',
    '试试记录一个月的开支，知道钱花在哪里，是规划的第一步。',
    'Try recording a month of spending: knowing where the money goes is the first step in planning.'
  ),
  ledgerKeep: L(
    '你已有記帳的習慣，這是訂立儲蓄目標最好的基礎。',
    '你已有记账的习惯，这是订立储蓄目标最好的基础。',
    'You already keep track of spending: the best base for setting savings goals.'
  ),
  parentHeadline: L(
    '作為家長，最重要的是無論發生甚麼事，家人的生活和孩子的將來都不受影響。',
    '作为家长，最重要的是无论发生什么事，家人的生活和孩子的将来都不受影响。',
    "As a parent, what matters most is that whatever happens, your family's life and your children's future stay on track."
  ),
  dependantHeadline: L(
    '有人依靠你，保障好自己，也就是保障了他們。',
    '有人依靠你，保障好自己，也就是保障了他们。',
    'Others rely on you, so protecting yourself protects them too.'
  ),
  soloHeadline: L(
    '現在正是為自己打好基礎的時候：越早開始，保費和儲蓄的壓力都越小。',
    '现在正是为自己打好基础的时候：越早开始，保费和储蓄的压力都越小。',
    'Now is the time to build your own base: the earlier you start, the lighter premiums and saving feel.'
  )
};

const MONEY_TIPS: Record<string, Txt> = {
  enjoy: L(
    '享受生活之餘，可以每月先把一部分收入自動轉去儲蓄，玩得更安心。',
    '享受生活之余，可以每月先把一部分收入自动转去储蓄，玩得更安心。',
    'While enjoying life, have part of your income moved to savings automatically each month, so the fun is worry-free.'
  ),
  save: L(
    '你習慣先儲蓄，下一步可以想想這些錢是為哪個目標準備。',
    '你习惯先储蓄，下一步可以想想这些钱是为哪个目标准备。',
    "You're used to saving first; next, think about which goal the money is for."
  ),
  invest: L(
    '投資之前，先預留相當於 3–6 個月開支的應急錢，遇到市況波動也不用急於套現。',
    '投资之前，先预留相当于 3–6 个月开支的应急钱，遇到市况波动也不用急于套现。',
    "Before investing, set aside 3–6 months of spending as an emergency fund, so market swings don't force you to sell."
  ),
  family: L(
    '你把家人放在第一位，也別忘了為自己預留保障和退休儲備。',
    '你把家人放在第一位，也别忘了为自己预留保障和退休储备。',
    "You put family first; don't forget cover and retirement savings for yourself."
  )
};

const FEELING_TIPS: Record<string, Txt> = {
  complex: L(
    '覺得保險複雜很正常：可以請顧問用生活例子解釋，不明白的條款直接問清楚。',
    '觉得保险复杂很正常：可以请顾问用生活例子解释，不明白的条款直接问清楚。',
    "It's normal to find insurance complicated: ask your adviser to explain with everyday examples, and ask about any term you don't follow."
  ),
  pushy: L(
    '了解保障不等於要立即購買，你可以先問清楚，再慢慢決定。',
    '了解保障不等于要立即购买，你可以先问清楚，再慢慢决定。',
    "Learning about cover doesn't mean buying right away: ask your questions first and decide in your own time."
  ),
  claims: L(
    '理賠是否順利，關鍵在投保時如實申報，並清楚了解不保事項。',
    '理赔是否顺利，关键在投保时如实申报，并清楚了解不保事项。',
    'Smooth claims come down to full disclosure when applying, and knowing clearly what is not covered.'
  )
};

/** The client's report, worded in `lang`. */
export const coverClientReport = (answers: CoverQuizAnswers, lang: QuizLang): CoverClientReport => {
  const signals = coverQuizSignals(answers, lang);
  const persona = answers.persona || 'individual';
  const picks = answers.picks || {};
  const multi = answers.multi || {};

  const dependantOptions = optionsFor(FAMILY_QUESTIONS[1], persona).filter(
    o => DEPENDANT_KEYS.includes(o.key) && (multi.D2 || []).includes(o.key)
  );

  const priorities: ReportPriority[] = [];
  const add = (p: ReportPriority) => {
    if (!priorities.some(existing => existing.key === p.key)) priorities.push(p);
  };
  if (dependantOptions.length) {
    add({
      key: 'income',
      icon: '🛡️',
      title: COPY.incomeTitle[lang],
      why: COPY.incomeWhy(
        listJoin(
          dependantOptions.map(o => o.label[lang]),
          lang
        )
      )[lang]
    });
  }
  const firstInterest = signals.interests[0];
  if (firstInterest) {
    add({
      key: `interest-${firstInterest.key}`,
      icon: firstInterest.icon,
      title: COPY.learn(firstInterest.label[lang])[lang],
      why: COPY.firstInterestWhy[lang]
    });
  }
  signals.life.forEach(o => {
    const p = LIFE_PRIORITIES[o.key];
    if (p) add({ key: `life-${o.key}`, icon: p.icon, title: p.title[lang], why: p.why[lang] });
  });
  if (signals.knowledge.wrong.some(q => q.id === 'B2')) {
    add({ key: 'medCi', icon: '🏥', title: COPY.medCiTitle[lang], why: COPY.medCiWhy[lang] });
  }
  signals.interests.slice(1).forEach(o =>
    add({
      key: `interest-${o.key}`,
      icon: o.icon,
      title: COPY.learn(o.label[lang])[lang],
      why: COPY.moreInterestWhy[lang]
    })
  );

  const tips: ReportTip[] = [];
  // Was the 「錢放銀行最安心」 swipe card; 收到 10 萬元會「存起來安心」 says the same.
  if (picks.C2 === 'save') tips.push({ key: 'inflation', icon: '📉', text: COPY.inflation[lang] });
  if (picks.E3 === 'unaware') {
    tips.push({ key: 'ledger', icon: '📒', text: COPY.ledgerStart[lang] });
  } else if (picks.E3 === 'ledger') {
    tips.push({ key: 'ledger', icon: '📒', text: COPY.ledgerKeep[lang] });
  }
  if (picks.C2 && MONEY_TIPS[picks.C2]) {
    tips.push({ key: `money-${picks.C2}`, icon: '💰', text: MONEY_TIPS[picks.C2][lang] });
  }
  if (picks.E4 && FEELING_TIPS[picks.E4]) {
    tips.push({ key: `feel-${picks.E4}`, icon: '💬', text: FEELING_TIPS[picks.E4][lang] });
  }

  const c3 = LIFE_QUESTIONS.find(q => q.id === 'C3');
  const dreamOption = c3 && picks.C3 ? optionsFor(c3, persona).find(o => o.key === picks.C3) : undefined;

  const headline = answers.role
    ? ROLE_HEADLINES[answers.role][lang]
    : persona === 'parent'
    ? COPY.parentHeadline[lang]
    : dependantOptions.length
    ? COPY.dependantHeadline[lang]
    : COPY.soloHeadline[lang];

  return {
    headline,
    priorities: priorities.slice(0, PRIORITY_MAX),
    tips: tips.slice(0, TIP_MAX),
    dream: dreamOption ? { icon: dreamOption.icon, label: dreamOption.label[lang] } : null
  };
};
